import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, ArrowRight, GraduationCap } from "lucide-react";

const empty = {
  first_name: "",
  last_name: "",
  grade: "",
  date_of_birth: "",
  disability_category: "",
  academic_level: "",
  case_manager: "",
  notes: "",
};

export default function Students() {
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/students");
    setStudents(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.first_name || !form.last_name || !form.grade) {
      toast.error("First name, last name, and grade are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/students", form);
      toast.success("Student added");
      setForm(empty);
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not add student");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="students-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="overline">Caseload</div>
          <h1 className="font-heading text-4xl font-medium ink mt-3">Students</h1>
          <p className="ink-muted mt-2 max-w-xl">Your active roster. Open any student to view their IEP, goals, lessons, and meetings.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#6E5AA0] hover:bg-[#574785] text-white h-11 px-5" data-testid="add-student-button">
              <Plus size={16} className="mr-2" /> Add student
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">New student</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-first-name" />
              </div>
              <div className="space-y-2">
                <Label>Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="student-last-name" />
              </div>
              <div className="space-y-2">
                <Label>Grade</Label>
                <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g., 4" data-testid="student-grade" />
              </div>
              <div className="space-y-2">
                <Label>Date of birth</Label>
                <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="student-dob" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Disability category</Label>
                <Input value={form.disability_category} onChange={(e) => setForm({ ...form, disability_category: e.target.value })} placeholder="e.g., Specific Learning Disability" data-testid="student-disability" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Academic level</Label>
                <Input value={form.academic_level} onChange={(e) => setForm({ ...form, academic_level: e.target.value })} placeholder="e.g., Reading at 2nd grade level" data-testid="student-academic-level" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Case manager</Label>
                <Input value={form.case_manager} onChange={(e) => setForm({ ...form, case_manager: e.target.value })} data-testid="student-case-manager" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="student-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="student-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="student-save">
                {saving ? "Saving…" : "Save student"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {loading && <div className="ink-muted calm-pulse">Loading roster…</div>}

      {!loading && students.length === 0 && (
        <Card className="p-12 border border-dashed border-[#D8D9D5] bg-white text-center" data-testid="students-empty">
          <div className="w-12 h-12 rounded-full bg-[#EFEAF5] flex items-center justify-center mx-auto mb-4">
            <GraduationCap size={20} className="text-[#6E5AA0]" />
          </div>
          <h3 className="font-heading text-xl font-medium ink">Add your first student</h3>
          <p className="ink-muted mt-2 max-w-md mx-auto">Once a student is added, you can draft an IEP with AI assistance, set goals, and start tracking progress.</p>
        </Card>
      )}

      {!loading && students.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {students.map((s) => (
            <Link key={s.id} to={`/students/${s.id}`} data-testid={`student-card-${s.id}`}>
              <Card className="p-6 border border-[#E8E9E6] bg-white shadow-sm lift">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading text-xl font-medium ink">{s.first_name} {s.last_name}</div>
                    <div className="text-sm ink-muted mt-1">Grade {s.grade}</div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-[#EFEAF5] flex items-center justify-center text-[#6E5AA0] font-medium">
                    {s.first_name?.[0]}{s.last_name?.[0]}
                  </div>
                </div>
                {s.disability_category && (
                  <div className="mt-4 inline-flex text-xs font-medium px-2.5 py-1 rounded-full bg-[#EFEAF5] text-[#6E5AA0]">
                    {s.disability_category}
                  </div>
                )}
                {s.academic_level && (
                  <p className="text-sm ink-muted mt-3 line-clamp-2">{s.academic_level}</p>
                )}
                <div className="mt-5 flex items-center text-sm text-[#6E5AA0] font-medium">
                  Open file <ArrowRight size={14} className="ml-1.5" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
